import React, { useCallback, useState } from "react";

interface ScrollManagerConfig {
  dataWidth: number;
  visibleColumns: number;
  scrollableColumns: number;
  featureWidth: number;
  shouldScroll: boolean;
}

interface ScrollState {
  isScrolled: boolean;
  canScrollRight: boolean;
  canScrollLeft: boolean;
  currentColumnIndex: number;
  hasScrollableContent: boolean;
}

export const useScrollManager = (
  tableContainerRef: React.RefObject<HTMLDivElement>,
  columnWidthConfig: ScrollManagerConfig
) => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    isScrolled: false,
    canScrollRight: false,
    canScrollLeft: false,
    currentColumnIndex: 0,
    hasScrollableContent: false,
  });

  // Column-aligned scrolling
  const scrollToColumn = useCallback(
    (columnIndex: number) => {
      if (!tableContainerRef.current || !columnWidthConfig.shouldScroll) return;

      // Prevent division by zero
      if (columnWidthConfig.dataWidth <= 0) return;

      const container = tableContainerRef.current;
      const targetScrollLeft = columnIndex * columnWidthConfig.dataWidth;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });

      setScrollState((prev) => ({ ...prev, currentColumnIndex: columnIndex }));
    },
    [tableContainerRef, columnWidthConfig.dataWidth, columnWidthConfig.shouldScroll]
  );

  const scrollLeft = useCallback(() => {
    if (!columnWidthConfig.shouldScroll || !scrollState.hasScrollableContent) return;

    if (!tableContainerRef.current) return;
    const container = tableContainerRef.current;
    const currentScrollLeft = container.scrollLeft;

    // For left button: use Math.ceil() to bias toward higher column index when between columns
    // This ensures user can navigate back from partial column views
    // Prevent division by zero
    const currentIndex = columnWidthConfig.dataWidth > 0
      ? Math.ceil(currentScrollLeft / columnWidthConfig.dataWidth)
      : 0;

    // Allow scrolling left even from first column to ensure consistent behavior
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToColumn(newIndex);
  }, [columnWidthConfig.shouldScroll, columnWidthConfig.dataWidth, scrollState.hasScrollableContent, tableContainerRef, scrollToColumn]);

  const scrollRight = useCallback(() => {
    if (!columnWidthConfig.shouldScroll || !scrollState.hasScrollableContent) return;

    if (!tableContainerRef.current) return;
    const container = tableContainerRef.current;
    const currentScrollLeft = container.scrollLeft;

    // For right button: use Math.floor() to bias toward lower column index when between columns
    // This ensures consistent forward navigation from current visual position
    // Prevent division by zero
    const currentIndex = columnWidthConfig.dataWidth > 0
      ? Math.floor(currentScrollLeft / columnWidthConfig.dataWidth)
      : 0;

    const maxScrollableIndex = Math.max(0, columnWidthConfig.scrollableColumns - columnWidthConfig.visibleColumns);

    if (currentIndex >= maxScrollableIndex) return;

    const newIndex = Math.min(maxScrollableIndex, currentIndex + 1);
    scrollToColumn(newIndex);
  }, [
    columnWidthConfig.shouldScroll,
    columnWidthConfig.dataWidth,
    columnWidthConfig.scrollableColumns,
    columnWidthConfig.visibleColumns,
    scrollState.hasScrollableContent,
    tableContainerRef,
    scrollToColumn,
  ]);

  // Unified function to update all scroll states with optimized checks
  const updateScrollState = useCallback(() => {
    if (!tableContainerRef.current) return;

    const container = tableContainerRef.current;
    const scrollLeft = Math.max(0, container.scrollLeft || 0);
    const scrollWidth = Math.max(0, container.scrollWidth || 0);
    const clientWidth = Math.max(0, container.clientWidth || 0);

    // Validate that we have meaningful dimensions
    if (clientWidth === 0 || scrollWidth === 0) return;

    const newIsScrolled = scrollLeft > 0;

    const hasScrollableContent = columnWidthConfig.shouldScroll && scrollWidth > clientWidth + 1; // Add 1px tolerance
    const newCanScrollLeft = hasScrollableContent && scrollLeft > 1; // Add 1px tolerance
    const newCanScrollRight = hasScrollableContent && scrollLeft + clientWidth < scrollWidth - 2; // Add 2px tolerance

    // Calculate current column index based on scroll position (only in scroll mode)
    let newCurrentColumnIndex = 0;
    if (columnWidthConfig.shouldScroll && columnWidthConfig.dataWidth > 0 && hasScrollableContent) {
      const calculatedIndex = Math.floor(scrollLeft / columnWidthConfig.dataWidth);
      const maxIndex = Math.max(0, columnWidthConfig.scrollableColumns - columnWidthConfig.visibleColumns);

      // Ensure all values are valid numbers
      if (Number.isFinite(calculatedIndex) && Number.isFinite(maxIndex)) {
        newCurrentColumnIndex = Math.max(0, Math.min(calculatedIndex, maxIndex));
      }
    }

    // Only update state if values actually changed
    setScrollState((prev) => {
      const hasChanged =
        prev.isScrolled !== newIsScrolled ||
        prev.canScrollLeft !== newCanScrollLeft ||
        prev.canScrollRight !== newCanScrollRight ||
        prev.currentColumnIndex !== newCurrentColumnIndex ||
        prev.hasScrollableContent !== hasScrollableContent;

      if (!hasChanged) return prev;

      return {
        isScrolled: newIsScrolled,
        canScrollLeft: newCanScrollLeft,
        canScrollRight: newCanScrollRight,
        currentColumnIndex: newCurrentColumnIndex,
        hasScrollableContent: hasScrollableContent,
      };
    });
  }, [tableContainerRef, columnWidthConfig.shouldScroll, columnWidthConfig.dataWidth, columnWidthConfig.scrollableColumns, columnWidthConfig.visibleColumns]);

  // Set up scroll event listener with proper cleanup
  React.useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const abortController = new AbortController();
    let scrollAnimationFrame: number | null = null;

    const throttledScrollHandler = () => {
      if (scrollAnimationFrame) return;

      scrollAnimationFrame = requestAnimationFrame(() => {
        updateScrollState();
        scrollAnimationFrame = null;
      });
    };

    container.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
      signal: abortController.signal,
    });
    updateScrollState(); // Initial check

    return () => {
      abortController.abort();
      if (scrollAnimationFrame) {
        cancelAnimationFrame(scrollAnimationFrame);
        scrollAnimationFrame = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateScrollState]);

  // Reset scroll state when switching between fill/scroll modes
  React.useLayoutEffect(() => {
    if (!columnWidthConfig.shouldScroll && tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = 0;
      setScrollState({
        isScrolled: false,
        canScrollRight: false,
        canScrollLeft: false,
        currentColumnIndex: 0,
        hasScrollableContent: false,
      });
    } else if (columnWidthConfig.shouldScroll) {
      // Force immediate state update when entering scroll mode
      const timeoutId = setTimeout(() => updateScrollState(), 0);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidthConfig.shouldScroll, columnWidthConfig.dataWidth, columnWidthConfig.scrollableColumns, columnWidthConfig.visibleColumns, updateScrollState]);

  return {
    ...scrollState,
    scrollToColumn,
    scrollLeft,
    scrollRight,
    updateScrollState,
  };
};